import React, { useEffect, useRef, useState } from 'react';
import { Shield, FileText, Target, Terminal, ArrowRight, Code, CheckCircle, Rocket, Search, Bug, Lock, MessageSquare, AlertTriangle, Zap, Database, Cloud, Users, RefreshCcw, Activity, Eye, Brain, ShieldCheck, AlertCircle, BarChart, Layers } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import TextCarousel from '@/components/ui/TextCarousel';

const SecurityFeatures = () => {
  const featuresRef = useRef<HTMLDivElement>(null);
  const textWrapperRef = useRef<HTMLSpanElement>(null);
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [wrapperWidth, setWrapperWidth] = useState('auto');
  const navigate = useNavigate();

  const rotatingServices = [
    "Vulnerability Management",
    "Penetration Testing", 
    "DevSecOps",
    "Application Security",
    "Cloud Security",
    "Security Advisory Services",
    "vCISO",
    "Cyber Threat Intelligence",
    "Security Automation & Orchestration",
    "Governance Risk & Compliance"
  ];

  // Match the exact services from Hero with consistent colors
  const securityServices = [
    {
      icon: Code,
      title: "Application Security",
      description: "Comprehensive application security testing throughout the SDLC. From static code analysis to runtime protection, we secure your applications at every layer.",
      color: "#00a86b",
      technologies: ["Checkmarx", "Veracode", "SonarQube", "OWASP ZAP", "Burp Suite", "Fortify"],
      keyFeatures: [
        "SAST/DAST/IAST implementation",
        "Dependency scanning (SCA)",
        "API security testing",
        "Mobile app security",
        "Secure code review"
      ],
      metrics: "85% vulnerability reduction",
      link: "/services/application-security"
    },
    {
      icon: Terminal,
      title: "DevSecOps",
      description: "Transform your development lifecycle with integrated security. Automate security testing, policy enforcement, and compliance checks directly in your CI/CD pipelines.",
      color: "#06b6d4",
      technologies: ["Jenkins", "GitLab CI", "Azure DevOps", "GitHub Actions", "Terraform", "Ansible"],
      keyFeatures: [
        "Pipeline security automation",
        "Infrastructure as Code scanning",
        "Container security integration",
        "Secrets management",
        "Policy as code implementation"
      ],
      metrics: "95% faster deployments",
      link: "/services/devsecops"
    },
    {
      icon: Shield,
      title: "Offensive Security",
      description: "Real-world attack simulations to identify vulnerabilities before malicious actors do. Our certified ethical hackers test your defenses using the latest techniques.",
      color: "#fbbf24",
      technologies: ["Metasploit", "Nmap", "Burp Suite", "Cobalt Strike", "Custom Exploits", "Kali Linux"],
      keyFeatures: [
        "Web application testing",
        "Network security testing",
        "Cloud infrastructure testing",
        "Social engineering assessment",
        "Red team exercises"
      ],
      metrics: "15 critical findings average",
      link: "/services/offensive-security"
    },
    {
      icon: Cloud,
      title: "Cloud Security",
      description: "Secure your cloud infrastructure across AWS, Azure, and GCP. From misconfigurations to compliance, we ensure your cloud environment is protected.",
      color: "#3b82f6",
      technologies: ["Wiz", "Prisma Cloud", "AWS Security Hub", "Azure Defender", "Cloud Custodian", "CSPM"],
      keyFeatures: [
        "Multi-cloud security posture",
        "Container & K8s security",
        "Infrastructure compliance",
        "Cloud workload protection",
        "Identity & access management"
      ],
      metrics: "99.9% compliance rate",
      link: "/services/cloud-security"
    },
    {
      icon: Users,
      title: "Security Advisory",
      description: "Expert security guidance and consultation services. Our team provides strategic recommendations and risk assessments tailored to your business needs.",
      color: "#a855f7",
      technologies: ["Risk Frameworks", "Security Standards", "Industry Best Practices", "Compliance Guidelines"],
      keyFeatures: [
        "Security architecture review",
        "Risk assessment & mitigation",
        "Security strategy development",
        "Incident response planning",
        "Security awareness programs"
      ],
      metrics: "90% risk reduction",
      link: "/services/security-advisory"
    }
  ];

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);


  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1
    });

    if (featuresRef.current) {
      const elements = featuresRef.current.querySelectorAll('.feature-item');
      elements.forEach(el => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }


          @keyframes float {
            0%, 100% {
              transform: translateY(0px) rotate(0deg);
            }
            33% {
              transform: translateY(-10px) rotate(-1deg);
            }
            66% {
              transform: translateY(-5px) rotate(1deg);
            }
          }

          @keyframes pulse {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.8;
              transform: scale(1.05);
            }
          }

          .animate-in {
            animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }

          .feature-item {
            opacity: 0;
          }

          .service-card {
            transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
            transform-style: preserve-3d;
          }

          .service-card:hover {
            transform: translateY(-8px);
          }

          .icon-glow {
            filter: drop-shadow(0 0 20px currentColor);
          }

          .gradient-text {
            background: linear-gradient(135deg, #00a86b 0%, #06b6d4 50%, #00a86b 100%);
            background-size: 200% 200%;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }

        `}
      </style>

      <section
        id="services"
        className="relative overflow-hidden pt-12 pb-24 bg-[#0f0f17]"
        ref={featuresRef}
      >
        {/* Background effects matching Hero */}
        <div className="absolute inset-0">
          {/* Base gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f0f17] via-[#0a0a12] to-[#0f0f17]" />
          
          {/* Orbs matching Hero style */}
          <div 
            className="absolute top-1/4 right-1/4 w-[500px] h-[500px] rounded-full opacity-20"
            style={{
              background: 'radial-gradient(circle at 30% 30%, rgba(0, 168, 107, 0.12) 0%, rgba(6, 182, 212, 0.08) 50%, transparent 70%)',
              filter: 'blur(60px)',
              animation: 'pulse 8s ease-in-out infinite'
            }}
          />
          <div 
            className="absolute bottom-1/3 left-1/3 w-[400px] h-[400px] rounded-full opacity-[0.15]"
            style={{
              background: 'radial-gradient(circle at 70% 70%, rgba(251, 191, 36, 0.10) 0%, rgba(168, 85, 247, 0.06) 50%, transparent 80%)',
              filter: 'blur(80px)',
              animation: 'pulse 10s ease-in-out infinite 1s'
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16 feature-item">
            
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="text-white">Cybersecurity Services</span>
              <br />
              <span className="gradient-text">Delivered by Experts</span>
            </h2>
            
            <div className="text-xl text-gray-300 max-w-3xl mx-auto text-center" style={{ overflow: 'visible' }}>
              <div className="mb-6" style={{ overflow: 'visible' }}>
                <TextCarousel 
                  texts={rotatingServices}
                  interval={3000}
                  textClassName="bg-gradient-to-r from-green-400 to-cyan-400 bg-clip-text text-transparent font-bold"
                  wrapperStyle={{ 
                    fontSize: '2rem', 
                    fontWeight: 'bold' 
                  }}
                />
              </div>
            </div>
          </div>

          {/* Service Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {securityServices.map((service, index) => {
              const Icon = service.icon;
              const isHovered = hoveredFeature === index;
              
              return (
                <div
                  key={index}
                  className="feature-item service-card group cursor-pointer"
                  style={{
                    animationDelay: `${index * 0.1}s`
                  }}
                  onMouseEnter={() => setHoveredFeature(index)}
                  onMouseLeave={() => setHoveredFeature(null)}
                  onClick={() => navigate(service.link)}
                >
                  <div 
                    className="relative h-full p-8 rounded-2xl border backdrop-blur-sm overflow-hidden"
                    style={{
                      borderColor: isHovered ? service.color : `${service.color}30`,
                      backgroundColor: isHovered ? `${service.color}15` : `${service.color}08`,
                      boxShadow: isHovered ? `0 20px 40px ${service.color}20` : 'none'
                    }}
                  >
                    {/* Background gradient on hover */}
                    <div 
                      className="absolute inset-0 opacity-0 transition-opacity duration-500"
                      style={{
                        background: `radial-gradient(circle at 50% 0%, ${service.color}20 0%, transparent 70%)`,
                        opacity: isHovered ? 1 : 0
                      }}
                    />

                    <div className="relative z-10">
                      {/* Icon */}
                      <div className="mb-6">
                        <div 
                          className={`inline-flex p-3 rounded-xl ${isHovered ? 'animate-float icon-glow' : ''}`}
                          style={{
                            backgroundColor: `${service.color}20`,
                            color: service.color
                          }}
                        >
                          <Icon className={`w-8 h-8 transition-transform duration-300 ${isHovered ? 'scale-110' : ''}`} />
                        </div>
                      </div>

                      {/* Content */}
                      <h3 className="text-xl font-bold text-white mb-3">
                        {service.title}
                      </h3>
                      
                      <p className="text-gray-400 mb-6 line-clamp-3">
                        {service.description}
                      </p>

                      {/* Metrics */}
                      <div 
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-300"
                        style={{
                          backgroundColor: `${service.color}20`,
                          color: service.color,
                          transform: isHovered ? 'scale(1.05)' : 'scale(1)'
                        }}
                      >
                        <Zap className="w-3 h-3" />
                        {service.metrics}
                      </div>

                    </div>
                  </div>
                </div>
              );
            })}

            {/* View All Services Card */}
            <div
              className="feature-item service-card group cursor-pointer"
              style={{
                animationDelay: `${securityServices.length * 0.1}s`
              }}
              onMouseEnter={() => setHoveredFeature(securityServices.length)}
              onMouseLeave={() => setHoveredFeature(null)}
              onClick={() => navigate('/services')}
            >
              <div 
                className="relative h-full p-8 rounded-2xl border backdrop-blur-sm overflow-hidden flex flex-col items-center justify-center text-center"
                style={{
                  borderColor: hoveredFeature === securityServices.length ? '#06b6d4' : '#06b6d430',
                  backgroundColor: hoveredFeature === securityServices.length ? '#06b6d415' : '#06b6d408',
                  boxShadow: hoveredFeature === securityServices.length ? '0 20px 40px #06b6d420' : 'none'
                }}
              >
                {/* Background gradient on hover */}
                <div 
                  className="absolute inset-0 opacity-0 transition-opacity duration-500"
                  style={{
                    background: 'radial-gradient(circle at 50% 0%, #06b6d420 0%, transparent 70%)',
                    opacity: hoveredFeature === securityServices.length ? 1 : 0
                  }}
                />

                <div className="relative z-10">
                  {/* Icon */}
                  <div className="mb-6">
                    <div 
                      className={`inline-flex p-3 rounded-xl ${hoveredFeature === securityServices.length ? 'animate-float icon-glow' : ''}`}
                      style={{
                        backgroundColor: '#06b6d420',
                        color: '#06b6d4'
                      }}
                    >
                      <ArrowRight className={`w-8 h-8 transition-transform duration-300 ${hoveredFeature === securityServices.length ? 'scale-110' : ''}`} />
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-white mb-3">
                    View All Services
                  </h3>
                  
                  <p className="text-gray-400 mb-6">
                    Explore our complete portfolio of cybersecurity services and find the perfect solution for your needs.
                  </p>

                  {/* CTA Badge */}
                  <div 
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-300"
                    style={{
                      backgroundColor: '#06b6d420',
                      color: '#06b6d4',
                      transform: hoveredFeature === securityServices.length ? 'scale(1.05)' : 'scale(1)'
                    }}
                  >
                    <Eye className="w-3 h-3" />
                    Discover More
                  </div>

                </div>
              </div>
            </div>
          </div>

        </div>
      </section>
    </>
  );
};

export default SecurityFeatures;